import React, { useState, useRef, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import { Button } from './ui/button';
import { Element as ElementType, DeconstructState } from '../types';
import { Loader2 } from 'lucide-react';
import { MdCleaningServices } from 'react-icons/md';
import { FaStar } from 'react-icons/fa';
import { FaLock, FaLockOpen } from 'react-icons/fa';
import { useInteractionMode } from '../contexts/InteractionModeContext';
import { useGameState } from '@/contexts/GameStateContext';
import { useStatus } from '@/contexts/StatusContext';
import MachineSlot from './MachineSlot';
import DeconstructWarning from './DeconstructWarning';
import { motion, AnimatePresence } from 'framer-motion';
import './animations.css';

interface DeconstructMachineProps {
  state: DeconstructState;
  onDrop: (element: ElementType) => void;
  onDeconstruct: () => Promise<void>;
  onClear: () => void;
  newDiscoveries?: Record<string, boolean>;
  onSelectOutput?: (element: ElementType) => void;
  isDeconstructionTried?: boolean;
}

const DeconstructMachine: React.FC<DeconstructMachineProps> = ({
  state,
  onDrop,
  onDeconstruct,
  onClear,
  newDiscoveries = {},
  onSelectOutput,
  isDeconstructionTried = false,
}) => {
  const { keyCount } = useGameState();
  const { mode, selectedElement, setSelectedElement } = useInteractionMode();
  const [selectedOutputIndex, setSelectedOutputIndex] = useState<number | null>(null);
  const [isLocked, setIsLocked] = useState<boolean>(true);
  const [showUnlockAnimation, setShowUnlockAnimation] = useState<boolean>(false);
  const [showInterface, setShowInterface] = useState<boolean>(false);
  const machineRef = useRef<HTMLDivElement>(null);

  // Handle tap destination for input slot
  const handleTapInput = () => {
    if (mode === 'tap-to-location' && selectedElement && !state.outputs) {
      onDrop(selectedElement);
      setSelectedElement(null);
    }
  };

  const [{ isOver }, dropRef] = useDrop({
    accept: 'element',
    canDrop: () => !state.outputs, // Prevent dropping when outputs are present
    drop: (item: ElementType) => {
      onDrop(item);
      return { handled: true, name: 'DeconstructMachine-Input' };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const canDeconstruct = state.input !== null && !state.isProcessing;

  // Handle keyhole click
  const handleKeyholeClick = () => {
    if (keyCount > 0 && isLocked) {
      // Start unlock animation
      setShowUnlockAnimation(true);

      // After animation completes, show the interface
      setTimeout(() => {
        setIsLocked(false);
        setShowUnlockAnimation(false);
        setShowInterface(true);
      }, 800); // Animation duration
    }
  };

  // Reset lock state when the machine is cleared completely
  useEffect(() => {
    if (!state.input && !state.outputs) {
      setShowInterface(false);
      setSelectedOutputIndex(null); // Reset selected output index
      setTimeout(() => {
        setIsLocked(true);
      }, 300); // Transition duration
    }
  }, [state.input, state.outputs]);

  // Handle selecting an output
  const handleSelectOutput = (element: ElementType, index: number) => {
    if (onSelectOutput) {
      setSelectedOutputIndex(index);
      onSelectOutput(element);
    }
  };

  return (
    <div
      ref={machineRef}
      className="deconstructor-machine bg-card rounded-lg p-3 border border-border shadow-md relative"
    >
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-base font-bold">Deconstructor</h3>
        <div className="flex items-center gap-1">
          {showInterface && (
            <>
              {state.input && !state.outputs && (
                <>
                  {/* Show warning icon if deconstruction has been tried before */}
                  {isDeconstructionTried && canDeconstruct && (
                    <DeconstructWarning className="mr-1" />
                  )}
                  <Button
                    onClick={onDeconstruct}
                    disabled={!canDeconstruct || state.isProcessing}
                    className="h-6 px-2"
                    size="sm"
                  >
                    {state.isProcessing ? (
                      <>
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" /> Deconstructing...
                      </>
                    ) : (
                      'Deconstruct'
                    )}
                  </Button>
                </>
              )}

              {(state.input || state.outputs) && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClear}
                  className="h-6 w-6"
                  title="Clear machine"
                >
                  <MdCleaningServices className="h-3 w-3" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {/* Locked state - show lock icon */}
        {isLocked && !showUnlockAnimation && (
          <motion.div
            className="flex flex-col items-center justify-center min-h-[140px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className={`relative p-4 rounded-full cursor-pointer ${
                keyCount > 0
                  ? 'bg-yellow-500/10 hover:bg-yellow-500/20 transition-colors'
                  : 'bg-gray-500/10'
              }`}
              onClick={keyCount > 0 ? handleKeyholeClick : undefined}
            >
              <FaLock
                className={`h-12 w-12 ${
                  keyCount > 0 ? 'text-yellow-500 animate-shake-grow' : 'text-gray-400'
                } ${keyCount > 0 && 'hover:scale-110 transition-transform'}`}
              />
              {keyCount > 0 && (
                <div className="absolute -top-2 -right-2 bg-yellow-500 text-yellow-950 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                  {keyCount}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {keyCount > 0
                ? 'Click to unlock with a key'
                : 'Collect keys to unlock the Deconstructor'}
            </p>
          </motion.div>
        )}

        {/* Unlock animation */}
        {showUnlockAnimation && (
          <motion.div
            className="flex flex-col items-center justify-center min-h-[140px]"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="relative p-4 rounded-full bg-yellow-500/20">
              <motion.div
                initial={{ rotate: 0 }}
                animate={{ rotate: [0, -20, 20, -20, 0], scale: [1, 1.1, 1] }}
                transition={{ duration: 0.8 }}
              >
                <FaLockOpen className="h-12 w-12 text-yellow-500" />
              </motion.div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Unlocking...</p>
          </motion.div>
        )}

        {/* Unlocked state - show interface */}
        {showInterface && (
          <motion.div
            className="flex flex-col gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Input area */}
            <div
              ref={dropRef}
              onClick={handleTapInput}
              className={`machine-area ${isOver ? 'droppable-active' : ''} ${
                mode === 'tap-to-location' && selectedElement
                  ? 'cursor-pointer hover:bg-accent/50'
                  : ''
              }`}
              style={{ minHeight: '40px', maxHeight: '50px', overflow: 'hidden' }}
            >
              {state.input ? (
                <MachineSlot element={state.input} slotType="input" />
              ) : (
                <p className="text-muted-foreground text-center text-xs">
                  {mode === 'tap-to-location' && selectedElement
                    ? 'Tap to place'
                    : 'Drop element to deconstruct'}
                </p>
              )}
            </div>

            {/* Output areas - two outputs side by side */}
            <div className="grid grid-cols-2 gap-2">
              {/* First output slot */}
              <div
                className="machine-area"
                style={{ minHeight: '40px', maxHeight: '50px', overflow: 'hidden' }}
              >
                {state.outputs && state.outputs.length > 0 ? (
                  <div
                    className={`relative w-full h-full ${
                      selectedOutputIndex !== null && selectedOutputIndex !== 0
                        ? 'opacity-30 pointer-events-none'
                        : 'cursor-pointer hover:scale-105 transition-transform'
                    }`}
                    onClick={() => handleSelectOutput(state.outputs[0], 0)}
                    style={{ minHeight: '40px', height: '100%' }}
                  >
                    {newDiscoveries &&
                      state.outputs[0] &&
                      newDiscoveries[state.outputs[0].id] === true && (
                        <div className="absolute -top-1 -right-1 z-10">
                          <div className="animate-rock">
                            <FaStar className="text-yellow-400 text-lg drop-shadow-lg" />
                          </div>
                        </div>
                      )}
                    <MachineSlot element={state.outputs[0]} slotType="output" disableDrag={true} />
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center text-xs">Output 1</p>
                )}
              </div>

              {/* Second output slot */}
              <div
                className="machine-area"
                style={{ minHeight: '40px', maxHeight: '50px', overflow: 'hidden' }}
              >
                {state.outputs && state.outputs.length > 1 ? (
                  <div
                    className={`relative w-full h-full ${
                      selectedOutputIndex !== null && selectedOutputIndex !== 1
                        ? 'opacity-30 pointer-events-none'
                        : 'cursor-pointer hover:scale-105 transition-transform'
                    }`}
                    onClick={() => handleSelectOutput(state.outputs[1], 1)}
                    style={{ minHeight: '40px', height: '100%' }}
                  >
                    {newDiscoveries &&
                      state.outputs[1] &&
                      newDiscoveries[state.outputs[1].id] === true && (
                        <div className="absolute -top-1 -right-1 z-10">
                          <div className="animate-rock">
                            <FaStar className="text-yellow-400 text-lg drop-shadow-lg" />
                          </div>
                        </div>
                      )}
                    <MachineSlot element={state.outputs[1]} slotType="output" disableDrag={true} />
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center text-xs">Output 2</p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DeconstructMachine;
